import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { BookingsExport, ExportBookingRow, ExportFacility } from "./bookingsExportData";

/**
 * Report giornaliero delle prenotazioni (A4 verticale).
 *
 * Il documento usa Poppins (già nel bundle dell'app) e una griglia a
 * colonne fisse: ogni campo è una sezione con la propria tabella, gli
 * header di tabella si ripetono quando la sezione va a capo pagina.
 */

Font.register({
  family: "Poppins",
  fonts: [
    { src: require("../../../assets/fonts/Poppins-Regular.ttf"), fontWeight: 400 },
    { src: require("../../../assets/fonts/Poppins-Italic.ttf"), fontWeight: 400, fontStyle: "italic" },
    { src: require("../../../assets/fonts/Poppins-Medium.ttf"), fontWeight: 500 },
    { src: require("../../../assets/fonts/Poppins-SemiBold.ttf"), fontWeight: 600 },
    { src: require("../../../assets/fonts/Poppins-Bold.ttf"), fontWeight: 700 },
  ],
});

// Niente sillabazione: i nomi propri spezzati a metà sono illeggibili.
Font.registerHyphenationCallback((word) => [word]);

const COLORS = {
  primary: "#f25d0b",
  ink: "#0f172a",
  body: "#1e293b",
  muted: "#64748b",
  faint: "#94a3b8",
  line: "#e2e8f0",
  lineSoft: "#f1f5f9",
  surface: "#f8fafc",
  white: "#ffffff",
};

// Larghezze colonne in punti (la pagina utile è 595 - 2*36 = 523pt).
const COL = {
  time: 46,
  type: 88,
  bookedBy: 112,
  notes: 118,
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 48,
    paddingHorizontal: 36,
    fontFamily: "Poppins",
    fontSize: 8.5,
    color: COLORS.body,
    backgroundColor: COLORS.white,
  },

  // ── Intestazione ───────────────────────────────────────
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 10,
    borderBottomWidth: 1.5,
    borderBottomColor: COLORS.primary,
    marginBottom: 16,
  },
  logo: { height: 30, width: 47 },
  headerRight: { alignItems: "flex-end" },
  eyebrow: {
    fontSize: 6.5,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: COLORS.muted,
    marginBottom: 2,
  },
  structureName: { fontSize: 11, fontWeight: 700, color: COLORS.ink },
  structureMeta: { fontSize: 7.5, color: COLORS.muted, marginTop: 1 },

  // ── Titolo ─────────────────────────────────────────────
  titleBlock: { marginBottom: 14 },
  title: { fontSize: 17, fontWeight: 700, color: COLORS.ink, letterSpacing: -0.2 },
  subtitle: { fontSize: 8.5, color: COLORS.muted, marginTop: 2 },

  // ── Riepilogo ──────────────────────────────────────────
  kpiRow: { flexDirection: "row", gap: 8, marginBottom: 18 },
  kpi: {
    flex: 1,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 5,
    paddingVertical: 7,
    paddingHorizontal: 9,
  },
  kpiAccent: {
    backgroundColor: COLORS.ink,
    borderColor: COLORS.ink,
  },
  kpiLabel: {
    fontSize: 6.5,
    fontWeight: 600,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: COLORS.muted,
  },
  kpiLabelAccent: { color: "#cbd5e1" },
  kpiValueRow: { flexDirection: "row", alignItems: "center", gap: 5, marginTop: 2 },
  kpiValue: { fontSize: 15, fontWeight: 700, color: COLORS.ink },
  kpiValueAccent: { color: COLORS.white },
  kpiDot: { width: 7, height: 7, borderRadius: 3.5 },

  // ── Sezione campo ──────────────────────────────────────
  facility: { marginBottom: 14 },
  facilityHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  facilityTitleRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  facilityName: { fontSize: 10.5, fontWeight: 700, color: COLORS.ink },
  pill: {
    fontSize: 6.5,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: COLORS.muted,
    backgroundColor: COLORS.lineSoft,
    borderRadius: 8,
    paddingVertical: 1.5,
    paddingHorizontal: 6,
  },
  facilityCount: { fontSize: 7.5, color: COLORS.muted },

  // ── Tabella ────────────────────────────────────────────
  thead: {
    flexDirection: "row",
    backgroundColor: COLORS.surface,
    borderTopWidth: 1,
    borderTopColor: COLORS.line,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.line,
  },
  th: {
    fontSize: 6.5,
    fontWeight: 600,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    color: COLORS.muted,
    paddingVertical: 5,
    paddingHorizontal: 7,
  },
  tr: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLORS.lineSoft,
    alignItems: "stretch",
  },
  trLast: { borderBottomColor: COLORS.line },
  td: { paddingVertical: 5.5, paddingHorizontal: 7, justifyContent: "center" },
  cellTime: { width: COL.time },
  cellType: { width: COL.type, paddingLeft: 14, position: "relative" },
  cellBookedBy: { width: COL.bookedBy },
  cellPeople: { flex: 1 },
  cellNotes: { width: COL.notes },

  time: { fontSize: 9, fontWeight: 700, color: COLORS.ink },
  colorBar: {
    position: "absolute",
    left: 6,
    top: 6,
    bottom: 6,
    width: 3,
    borderRadius: 1.5,
  },
  typeText: { fontSize: 8, fontWeight: 500, color: COLORS.ink },
  primaryText: { fontSize: 8.5, fontWeight: 500, color: COLORS.ink },
  secondaryText: { fontSize: 7, color: COLORS.muted, marginTop: 1 },
  teamLine: { flexDirection: "row", alignItems: "center", gap: 4 },
  teamTag: {
    fontSize: 6,
    fontWeight: 600,
    color: COLORS.faint,
    width: 14,
  },
  teamText: { fontSize: 8, color: COLORS.body, flex: 1 },
  notesText: { fontSize: 7.5, color: COLORS.body, lineHeight: 1.35 },
  emptyText: { fontSize: 7.5, color: COLORS.faint },
  cancelledText: { color: COLORS.faint, textDecoration: "line-through" },
  cancelledTag: {
    fontSize: 6,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: "#b91c1c",
    marginTop: 1,
  },
  emptyRow: {
    paddingVertical: 9,
    paddingHorizontal: 7,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.line,
  },

  // ── Piè di pagina ──────────────────────────────────────
  footer: {
    position: "absolute",
    left: 36,
    right: 36,
    bottom: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: COLORS.lineSoft,
    paddingTop: 6,
    fontSize: 6.5,
    color: COLORS.faint,
  },
});

const Header: React.FC<{ data: BookingsExport }> = ({ data }) => (
  <View style={styles.header} fixed>
    <Image style={styles.logo} src={require("../../../assets/logo/logo.png")} />
    <View style={styles.headerRight}>
      <Text style={styles.eyebrow}>Report prenotazioni</Text>
      <Text style={styles.structureName}>{data.structureName}</Text>
      {!!data.structureAddress && (
        <Text style={styles.structureMeta}>{data.structureAddress}</Text>
      )}
      {!!data.structureContact && (
        <Text style={styles.structureMeta}>{data.structureContact}</Text>
      )}
    </View>
  </View>
);

const Footer: React.FC<{ data: BookingsExport }> = ({ data }) => (
  <View style={styles.footer} fixed>
    <Text>
      {data.structureName} · Prenotazioni del {data.dateShort} · Generato il{" "}
      {data.generatedAt}
    </Text>
    <Text
      render={({ pageNumber, totalPages }) =>
        `Pagina ${pageNumber} di ${totalPages}`
      }
    />
  </View>
);

const Summary: React.FC<{ data: BookingsExport }> = ({ data }) => {
  const { totals } = data;
  const types = totals.byType.slice(0, 4);
  return (
    <View style={styles.kpiRow}>
      <View style={[styles.kpi, styles.kpiAccent]}>
        <Text style={[styles.kpiLabel, styles.kpiLabelAccent]}>Prenotazioni</Text>
        <View style={styles.kpiValueRow}>
          <Text style={[styles.kpiValue, styles.kpiValueAccent]}>
            {totals.bookings}
          </Text>
        </View>
      </View>
      <View style={styles.kpi}>
        <Text style={styles.kpiLabel}>Campi in uso</Text>
        <View style={styles.kpiValueRow}>
          <Text style={styles.kpiValue}>
            {totals.facilitiesWithBookings}/{totals.facilities}
          </Text>
        </View>
      </View>
      {types.map((t) => (
        <View key={t.label} style={styles.kpi}>
          <Text style={styles.kpiLabel}>{t.label}</Text>
          <View style={styles.kpiValueRow}>
            <View style={[styles.kpiDot, { backgroundColor: t.color }]} />
            <Text style={styles.kpiValue}>{t.count}</Text>
          </View>
        </View>
      ))}
    </View>
  );
};

const TableHead: React.FC = () => (
  <View style={styles.thead}>
    <Text style={[styles.th, styles.cellTime]}>Orario</Text>
    <Text style={[styles.th, { width: COL.type }]}>Tipologia</Text>
    <Text style={[styles.th, styles.cellBookedBy]}>Prenotato da</Text>
    <Text style={[styles.th, styles.cellPeople]}>Partecipanti</Text>
    <Text style={[styles.th, styles.cellNotes]}>Note</Text>
  </View>
);

const People: React.FC<{ row: ExportBookingRow }> = ({ row }) => {
  const hasTeams = row.team1.length > 0 || row.team2.length > 0;
  if (!hasTeams && !row.coach) {
    return <Text style={styles.emptyText}>—</Text>;
  }
  return (
    <View>
      {row.team1.length > 0 && (
        <View style={styles.teamLine}>
          <Text style={styles.teamTag}>{row.team2.length > 0 ? "A" : ""}</Text>
          <Text style={styles.teamText}>{row.team1.join(" · ")}</Text>
        </View>
      )}
      {row.team2.length > 0 && (
        <View style={styles.teamLine}>
          <Text style={styles.teamTag}>B</Text>
          <Text style={styles.teamText}>{row.team2.join(" · ")}</Text>
        </View>
      )}
      {!!row.coach && (
        <Text style={styles.secondaryText}>Coach: {row.coach}</Text>
      )}
    </View>
  );
};

const Row: React.FC<{ row: ExportBookingRow; last: boolean }> = ({
  row,
  last,
}) => {
  const muted = row.isCancelled ? styles.cancelledText : undefined;
  return (
    <View style={[styles.tr, last ? styles.trLast : {}]} wrap={false}>
      <View style={[styles.td, styles.cellTime]}>
        <Text style={[styles.time, muted || {}]}>{row.time}</Text>
        {row.isCancelled && <Text style={styles.cancelledTag}>Annullata</Text>}
      </View>
      <View style={[styles.td, styles.cellType]}>
        <View style={[styles.colorBar, { backgroundColor: row.color }]} />
        <Text style={[styles.typeText, muted || {}]}>{row.typeLabel}</Text>
        {!!row.matchTypeLabel && (
          <Text style={styles.secondaryText}>{row.matchTypeLabel}</Text>
        )}
      </View>
      <View style={[styles.td, styles.cellBookedBy]}>
        <Text style={[styles.primaryText, muted || {}]}>{row.bookedBy}</Text>
        {!!row.contact && <Text style={styles.secondaryText}>{row.contact}</Text>}
        {row.scopeLabel === "Aperta" && (
          <Text style={styles.secondaryText}>Partita aperta</Text>
        )}
      </View>
      <View style={[styles.td, styles.cellPeople]}>
        <People row={row} />
      </View>
      <View style={[styles.td, styles.cellNotes]}>
        {row.notes ? (
          <Text style={styles.notesText}>{row.notes}</Text>
        ) : (
          <Text style={styles.emptyText}>—</Text>
        )}
      </View>
    </View>
  );
};

const FacilitySection: React.FC<{ facility: ExportFacility }> = ({
  facility,
}) => {
  const [firstRow, ...otherRows] = facility.rows;
  const total = facility.rows.length;
  return (
    <View style={styles.facility}>
      {/* Titolo, intestazione tabella e prima riga restano sempre insieme:
          così il nome del campo non finisce mai da solo a fondo pagina. */}
      <View wrap={false}>
        <View style={styles.facilityHeader}>
          <View style={styles.facilityTitleRow}>
            <Text style={styles.facilityName}>{facility.name}</Text>
            {!!facility.typeLabel && (
              <Text style={styles.pill}>{facility.typeLabel}</Text>
            )}
            {facility.isLesson && <Text style={styles.pill}>Lezioni</Text>}
          </View>
          <Text style={styles.facilityCount}>
            {total === 0
              ? "Nessuna prenotazione"
              : total === 1
              ? "1 prenotazione"
              : `${total} prenotazioni`}
          </Text>
        </View>
        <TableHead />
        {firstRow ? (
          <Row row={firstRow} last={total === 1} />
        ) : (
          <View style={styles.emptyRow}>
            <Text style={styles.emptyText}>
              Nessuna prenotazione registrata per questo campo.
            </Text>
          </View>
        )}
      </View>
      {otherRows.map((row, i) => (
        <Row
          key={`${row.time}-${i}`}
          row={row}
          last={i === otherRows.length - 1}
        />
      ))}
    </View>
  );
};

export const BookingsReportPdf: React.FC<{ data: BookingsExport }> = ({
  data,
}) => {
  const subtitleParts = [
    data.sportName,
    `${data.totals.facilities} ${data.totals.facilities === 1 ? "campo" : "campi"}`,
    data.weather
      ? `Meteo ${Math.round(data.weather.min)}° / ${Math.round(
          data.weather.max
        )}° · vento ${data.weather.windKmh} km/h`
      : "",
  ].filter(Boolean);

  return (
    <Document
      title={`Prenotazioni ${data.dateShort} – ${data.structureName}`}
      author={data.structureName}
      creator="GetMove"
      producer="GetMove"
      language="it"
    >
      <Page size="A4" style={styles.page}>
        <Header data={data} />

        <View style={styles.titleBlock}>
          <Text style={styles.title}>{data.dateLong}</Text>
          <Text style={styles.subtitle}>{subtitleParts.join("  ·  ")}</Text>
        </View>

        <Summary data={data} />

        {data.facilities.map((facility) => (
          <FacilitySection key={facility.id} facility={facility} />
        ))}

        <Footer data={data} />
      </Page>
    </Document>
  );
};

export default BookingsReportPdf;
